import React from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Bar,
  Tooltip,
  Cell,
  PieChart,
  Pie
} from 'recharts';
import { Sector } from 'recharts';

// 색상 상수 (원본과 동일 계열)
const DIFFICULTY_COLORS = [
  'rgba(253, 224, 71, 0.7)',   // Lv1 yellow-300
  'rgba(134, 239, 172, 0.7)',  // Lv2 green-300
  'rgba(147, 197, 253, 0.7)',  // Lv3 blue-300
  'rgba(253, 186, 116, 0.7)',  // Lv4 orange-300
  'rgba(252, 165, 165, 0.7)'   // Lv5 red-300
];

const HEX_COLORS = {
  lightGray: '#E5E7EB',
  gray600: '#4B5563',
  borderGray: '#E5E7EB',
  primary: '#4ABC85'
};

// result -> Map(problem_id -> boolean)
function getResultMap(test) {
  if (!test?.result) return new Map();
  return new Map(Object.entries(test.result).map(([pid, v]) => [parseInt(pid, 10), v]));
}

// 난이도별 정답률 데이터
// return: [{ level, currentAcc, currentCorrect, currentTotal }]
export function getLevelAchievementData(test) {
  if (!test) {
    return [1, 2, 3, 4, 5].map(level => ({
      level,
      currentAcc: 0,
      currentCorrect: 0,
      currentTotal: 0
    }));
  }

  const correctByLevel = [0, 0, 0, 0, 0];
  const totalByLevel = [0, 0, 0, 0, 0];

  if (Array.isArray(test.correctByLevel) && Array.isArray(test.totalByLevel)) {
    for (let level = 1; level <= 5; level++) {
      const idx = level - 1;
      totalByLevel[idx] = Number(test.totalByLevel[idx] || 0);
      correctByLevel[idx] = Number(test.correctByLevel[idx] || 0);
    }
  } else if (test.by_step) {
    for (let level = 1; level <= 5; level++) {
      const stepData = test.by_step[String(level)];
      if (Array.isArray(stepData) && stepData.length >= 2) {
        const [totalProblems, correctCount] = stepData;
        const idx = level - 1;
        totalByLevel[idx] = totalProblems || 0;
        correctByLevel[idx] = correctCount || 0;
      }
    }
  } else if (test.sequence || test.problems_list) {
    const resultMap = getResultMap(test);
    const problems = test.problems_list || test.sequence || [];
    for (const problem of problems) {
      let step, isCorrect;
      if (problem?.problem_id) {
        step = problem.step;
        isCorrect = resultMap.get(problem.problem_id);
      } else if (Array.isArray(problem)) {
        step = problem[1];
        isCorrect = problem[6];
      }
      if (step >= 1 && step <= 5) {
        const idx = step - 1;
        totalByLevel[idx]++;
        if (isCorrect) correctByLevel[idx]++;
      }
    }
  }

  return [1, 2, 3, 4, 5].map((level, idx) => {
    const total = totalByLevel[idx];
    const correct = correctByLevel[idx];
    const acc = total > 0 ? Math.round((correct / total) * 100) : 0;
    return {
      level,
      currentAcc: acc,
      currentCorrect: correct,
      currentTotal: total
    };
  });
}

// 난이도별 성취도 입력 데이터 (정답률 × 문제수 가중치)
function getLevelRadarDataByProblemCount(test) {
  if (!test) {
    return [1, 2, 3, 4, 5].map(level => ({
      subject: `Lv ${level}`,
      A: 0,
      problemCount: 0,
      correctCount: 0,
      correctRate: 0,
      fullMark: 100
    }));
  }

  const totals = [0, 0, 0, 0, 0];
  const corrects = [0, 0, 0, 0, 0];

  if (Array.isArray(test.correctByLevel) && Array.isArray(test.totalByLevel)) {
    for (let level = 1; level <= 5; level++) {
      const idx = level - 1;
      totals[idx] = Number(test.totalByLevel[idx] || 0);
      corrects[idx] = Number(test.correctByLevel[idx] || 0);
    }
  } else if (test.by_step) {
    for (let level = 1; level <= 5; level++) {
      const stepData = test.by_step[String(level)];
      if (Array.isArray(stepData) && stepData.length >= 2) {
        const [totalProblems, correctCount] = stepData;
        const idx = level - 1;
        totals[idx] = totalProblems || 0;
        corrects[idx] = correctCount || 0;
      }
    }
  } else if (test.sequence || test.problems_list) {
    const problems = test.problems_list || test.sequence || [];
    const resultMap = getResultMap(test);
    for (const problem of problems) {
      let step, isCorrect;
      if (problem?.problem_id) {
        step = problem.step;
        isCorrect = resultMap.get(problem.problem_id);
      } else if (Array.isArray(problem)) {
        step = problem[1];
        isCorrect = problem[6];
      }
      if (step >= 1 && step <= 5) {
        const idx = step - 1;
        totals[idx]++;
        if (isCorrect) corrects[idx]++;
      }
    }
  }

  const maxProblems = Math.max(...totals, 1);
  return [1, 2, 3, 4, 5].map((level, idx) => {
    const total = totals[idx];
    const correct = corrects[idx];
    const correctRate = total > 0 ? Math.round((correct / total) * 100) : 0;

    let achievement = 0;
    if (total > 0) {
      const weight = Math.sqrt(total / maxProblems);
      achievement = Math.round(correctRate * weight);
      if (correct > 0 && achievement < 10) {
        achievement = 10; // 최소 가시성 보장
      }
    }

    return {
      subject: `Lv ${level}`,
      A: achievement,
      problemCount: total,
      correctCount: correct,
      correctRate,
      fullMark: 100
    };
  });
}

// 도넛 차트용 데이터
export function getLevelPieData(test) {
  const data = getLevelRadarDataByProblemCount(test);
  return data.map(item => ({
    name: item.subject,
    value: item.A,
    problemCount: item.problemCount,
    correctCount: item.correctCount,
    correctRate: item.correctRate
  }));
}

// 도넛 차트 active 섹션 렌더
export function renderActiveShape(props) {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, payload, value } = props;
  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
        cornerRadius={8}
      />
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={outerRadius + 3}
        outerRadius={outerRadius + 6}
        startAngle={startAngle + 8}
        endAngle={endAngle - 8}
        fill={fill}
        fillOpacity={1}
        cornerRadius={8}
      />
      <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central" fill="#333">
        <tspan x={cx} dy="-0.8em" fontSize={10} fontWeight="bold">{payload.name}</tspan>
        <tspan x={cx} dy="1.4em" fontSize={8} fill="#666">성취도: {value}%</tspan>
        <tspan x={cx} dy="1.4em" fontSize={8} fill="#999">
          {payload.correctCount}/{payload.problemCount}문제
        </tspan>
      </text>
    </g>
  );
}

// Y축 라벨 색 (정답률은 primary)
function getAccuracyYAxisLabelColor() {
  return HEX_COLORS.primary;
}

// 난이도별 정답률 (막대)
export const DifficultyAccuracyChart = React.memo(function DifficultyAccuracyChart({ selectedTest }) {
  if (!selectedTest) return null;
  const chartData = getLevelAchievementData(selectedTest);

  return (
    <div className="col-span-1 rounded-3xl bg-gray-50 p-3">
      <h4 className="mb-2 text-xs font-medium text-gray-700">난이도별 정답률</h4>
      <div className="flex h-32 items-center justify-center">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={HEX_COLORS.lightGray} />
            <XAxis
              dataKey="level"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: HEX_COLORS.gray600 }}
              tickFormatter={(v) => `Lv${v}`}
              height={20}
              padding={{ left: 0, right: 0 }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              domain={[0, 100]}
              ticks={[0, 25, 50, 75, 100]}
              tick={(props) => {
                const { x, y, payload } = props;
                return (
                  <text x={x} y={y} textAnchor="end" fontSize={7} fill={getAccuracyYAxisLabelColor()} fontWeight="bolder" dx={-2}>
                    {payload.value}
                  </text>
                );
              }}
              width={15}
              tickMargin={-5}
            />
            <Tooltip
              isAnimationActive={false}
              contentStyle={{ fontSize: '10px', padding: '4px 6px', borderRadius: '4px', border: `1px solid ${HEX_COLORS.borderGray}`, backgroundColor: 'white' }}
              formatter={(value, _name, props) => {
                const correct = props.payload.currentCorrect;
                const total = props.payload.currentTotal;
                return [`${value}% (${correct}/${total})`, '정답률'];
              }}
              labelFormatter={(label) => `Lv${label}`}
            />
            <Bar dataKey="currentAcc" radius={[8, 8, 0, 0]}>
              {chartData.map((_, index) => (
                <Cell key={index} fill={DIFFICULTY_COLORS[index]} />
              ))}
            </Bar>
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
});

// 난이도별 성취도 (도넛)
export const DifficultyAchievementChart = React.memo(function DifficultyAchievementChart({
  selectedTest
}) {
  const [activePieIndex, setActivePieIndex] = React.useState(-1);
  const pieData = selectedTest ? getLevelPieData(selectedTest) : [];

  React.useEffect(() => {
    if (!selectedTest || pieData.length === 0) return;
    // 가장 큰 값을 가진 섹션 자동 선택
    let maxIdx = 0;
    let maxVal = pieData[0].value;
    for (let i = 1; i < pieData.length; i++) {
      if (pieData[i].value > maxVal) {
        maxVal = pieData[i].value;
        maxIdx = i;
      }
    }
    setActivePieIndex(maxIdx);
  }, [selectedTest]); // selectedTest 변경 시만

  if (!selectedTest) return null;

  return (
    <div className="col-span-1 rounded-3xl bg-gray-50 p-3">
      <h4 className="mb-2 text-xs font-medium text-gray-700">난이도별 성취도</h4>
      <div className="flex h-32 items-center justify-center">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              innerRadius="50%"
              outerRadius="90%"
              paddingAngle={2}
              dataKey="value"
              cornerRadius={8}
              activeIndex={activePieIndex}
              activeShape={renderActiveShape}
              onMouseEnter={(_, index) => setActivePieIndex(index)}
              onClick={(_, index) => setActivePieIndex(index)}
              onTouchEnd={(_, index) => setActivePieIndex(index)}
            >
              {pieData.map((_, index) => (
                <Cell key={index} fill={DIFFICULTY_COLORS[index]} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
});

// 패널(두 차트 묶음)
export default function DifficultyAnalysisPanel({ selectedTest, testHistory }) {
  const [isCumulative, setIsCumulative] = React.useState(false);
  
  // 누적 데이터 계산 (선택된 테스트까지의 누적)
  const cumulativeData = React.useMemo(() => {
    if (!testHistory || testHistory.length === 0 || !selectedTest) return null;
    
    const cumCorrectByLevel = [0, 0, 0, 0, 0];
    const cumTotalByLevel = [0, 0, 0, 0, 0];
    
    // 선택된 테스트의 날짜를 기준으로 그 이전 테스트들만 포함
    const selectedDate = new Date(selectedTest.updated_at || selectedTest.start_time || 0).getTime();
    
    // 테스트들을 날짜순으로 정렬 (오래된 것부터)
    const sortedTests = [...testHistory].sort((a, b) => {
      const dateA = new Date(a.updated_at || a.start_time || 0).getTime();
      const dateB = new Date(b.updated_at || b.start_time || 0).getTime();
      return dateA - dateB;
    });
    
    // 선택된 테스트까지의 데이터만 누적
    let testsIncluded = 0;
    for (const test of sortedTests) {
      const testDate = new Date(test.updated_at || test.start_time || 0).getTime();
      
      // 선택된 테스트 이후의 테스트는 포함하지 않음
      if (testDate > selectedDate) break;
      
      if (test.by_step) {
        testsIncluded++;
        for (let level = 1; level <= 5; level++) {
          const stepData = test.by_step[String(level)];
          if (Array.isArray(stepData) && stepData.length >= 2) {
            const [totalProblems, correctCount] = stepData;
            const idx = level - 1;
            cumTotalByLevel[idx] += totalProblems || 0;
            cumCorrectByLevel[idx] += correctCount || 0;
          }
        }
      }
    }
    
    // 데이터가 없으면 null 반환
    if (testsIncluded === 0) return null;
    
    return {
      correctByLevel: cumCorrectByLevel,
      totalByLevel: cumTotalByLevel,
      by_step: {
        '1': [cumTotalByLevel[0], cumCorrectByLevel[0]],
        '2': [cumTotalByLevel[1], cumCorrectByLevel[1]],
        '3': [cumTotalByLevel[2], cumCorrectByLevel[2]],
        '4': [cumTotalByLevel[3], cumCorrectByLevel[3]],
        '5': [cumTotalByLevel[4], cumCorrectByLevel[4]]
      },
      testsIncluded
    };
  }, [testHistory, selectedTest]);
  
  // 누적 버튼 활성화 시 누적 데이터, 비활성화 시 개별 데이터
  const displayData = isCumulative ? (cumulativeData || selectedTest) : selectedTest;
  
  if (!selectedTest) return null;
  
  return (
    <div className="rounded-3xl bg-white shadow-sm" style={{ boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)' }}>
      <div className="px-4 pt-4">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h3 className="text-[14px] font-semibold">난이도별 분석</h3>
            <button
              onClick={() => setIsCumulative(!isCumulative)}
              className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium transition-all ${
                isCumulative 
                  ? 'bg-[#4ABC85] text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <span>누적</span>
            </button>
          </div>
          <div className="rounded text-[9px] font-medium text-gray-500">
            {isCumulative 
              ? `누적 데이터 (${cumulativeData?.testsIncluded || 0}개 테스트)`
              : `TID ${selectedTest.exam_id} ${selectedTest.tree_name ? `| ${selectedTest.tree_name}` : ''}`
            }
          </div>
        </div>
      </div>
      <div className="px-[14.4px] pb-[14.4px]">
        <div className="grid grid-cols-2 gap-4">
          <DifficultyAccuracyChart selectedTest={displayData} />
          <DifficultyAchievementChart selectedTest={displayData} />
        </div>
      </div>
    </div>
  );
}


